import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Tag,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiBriefcase, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  LibraryElementTypeDto,
  type CompetitionPlanDto,
  type LibraryElementDto,
  type MarketDialogueQuestionDto,
  useGetCompetitionPlanQuery,
  useListMarketDialogueAnswersQuery,
  useStartCompetitionFromMarketDialogueMutation,
} from "../../autogen/bff-api";
import { useApiError } from "../../common/errors/useApiError";
import { AddRequirementPanel } from "../../common/library/AddRequirementPanel";
import { procurementEntryHref } from "../sourcing-event/procurementEntryHref";
import { TakenForwardAnswers } from "./TakenForwardAnswers";

interface Props {
  marketDialogueId: string;
  questions: MarketDialogueQuestionDto[];
}

export const StartCompetitionButton = ({
  marketDialogueId,
  questions,
}: Props) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <>
      <Button
        size="sm"
        colorScheme="teal"
        leftIcon={<Icon as={FiBriefcase} />}
        onClick={onOpen}
      >
        {t("Create procurement")}
      </Button>
      {isOpen && (
        <CreateProcurementModal
          marketDialogueId={marketDialogueId}
          questions={questions}
          onClose={onClose}
        />
      )}
    </>
  );
};

const CreateProcurementModal = ({
  marketDialogueId,
  questions,
  onClose,
}: Props & { onClose: () => void }) => {
  const { t } = useTranslation();
  const { data: plan, isLoading, isError, refetch } = useGetCompetitionPlanQuery({
    marketDialogueId,
  });
  return (
    <Modal isOpen onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t("Create procurement from the market dialogue")}
        </ModalHeader>
        <ModalCloseButton />
        {isLoading ? (
          <ModalBody>
            <Flex justify="center" py="10">
              <Spinner />
            </Flex>
          </ModalBody>
        ) : isError || !plan ? (
          <ModalBody pb="6">
            <LoadFailed
              message={t("Could not load what can follow the procurement.")}
              onRetry={() => void refetch()}
            />
          </ModalBody>
        ) : (
          <PlanForm
            key={marketDialogueId}
            marketDialogueId={marketDialogueId}
            questions={questions}
            plan={plan}
            onClose={onClose}
          />
        )}
      </ModalContent>
    </Modal>
  );
};

const PlanForm = ({
  marketDialogueId,
  questions,
  plan,
  onClose,
}: Props & { plan: CompetitionPlanDto; onClose: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trigger } = useApiError();
  const [start, { isLoading }] =
    useStartCompetitionFromMarketDialogueMutation();
  const [suppliers, setSuppliers] = useState(() =>
    plan.suppliers.map((s) => s.organizationId),
  );
  const [documents, setDocuments] = useState(() =>
    plan.documents.map((d) => d.id),
  );
  const [insightReport, setInsightReport] = useState(
    plan.insightReportAvailable,
  );
  const [requirements, setRequirements] = useState<LibraryElementDto[]>([]);
  const [openLibrary, setOpenLibrary] = useState<LibraryElementTypeDto | null>(
    null,
  );
  const librarySections = [
    {
      type: LibraryElementTypeDto.Minimum,
      button: t("Add minimum requirements"),
      noun: t("minimum requirements"),
    },
    {
      type: LibraryElementTypeDto.Selection,
      button: t("Add qualification requirements"),
      noun: t("qualification requirements"),
    },
    {
      type: LibraryElementTypeDto.Award,
      button: t("Add award criteria"),
      noun: t("award criteria"),
    },
  ];
  const section = librarySections.find((s) => s.type === openLibrary);

  const create = async () => {
    try {
      const procurement = await start({
        marketDialogueId,
        startCompetitionRequest: {
          supplierIds: suppliers,
          documentIds: documents,
          insightReport,
          libraryElementIds: requirements.map((r) => r.id),
        },
      }).unwrap();
      onClose();
      navigate(procurementEntryHref(procurement.status, procurement.id));
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <>
      <ModalBody>
        <Stack spacing="6">
          <Text color="smMuted">
            {t(
              "The title, description, classification and estimated value always follow. Choose what else follows.",
            )}
          </Text>
          <ChoiceSection
            title={t("Suppliers")}
            help={t(
              "They are suggested for invitation in an invited competition, and told by email when an announced competition is published.",
            )}
            empty={t(
              "No supplier has answered, asked a question or been invited.",
            )}
            items={plan.suppliers.map((s) => ({
              id: s.organizationId,
              label: s.name,
              tags: [
                s.answered ? t("Answered") : null,
                s.asked ? t("Asked a question") : null,
                s.invited ? t("Invited") : null,
              ],
            }))}
            selected={suppliers}
            onChange={setSuppliers}
          />
          <ChoiceSection
            title={t("Documents")}
            empty={t("The market dialogue has no documents.")}
            items={plan.documents.map((d) => ({
              id: d.id,
              label: d.name,
              tags: [],
            }))}
            selected={documents}
            onChange={setDocuments}
          />
          {plan.insightReportAvailable && (
            <Box>
              <Heading as="h3" size="sm" mb="2">
                {t("Insight report")}
              </Heading>
              <Checkbox
                isChecked={insightReport}
                onChange={(e) => setInsightReport(e.target.checked)}
              >
                {t("Attach the insight report as a text document")}
              </Checkbox>
              <Text fontSize="sm" color="smMuted" mt="1">
                {t(
                  "Answers marked as business secrets are left out of the report.",
                )}
              </Text>
            </Box>
          )}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="6">
            <Box>
              <Heading as="h3" size="sm" mb="2">
                {t("Requirements and criteria from the requirement library")}
              </Heading>
              <HStack spacing="2" flexWrap="wrap" mb="3">
                {librarySections.map((s) => (
                  <Button
                    key={s.type}
                    size="sm"
                    variant="outline"
                    onClick={() => setOpenLibrary(s.type)}
                  >
                    {s.button}
                  </Button>
                ))}
              </HStack>
              {requirements.length === 0 ? (
                <Text fontSize="sm" color="smMuted">
                  {t("No requirements chosen.")}
                </Text>
              ) : (
                <Stack spacing="1" data-testid="chosen-requirements">
                  {requirements.map((r) => (
                    <HStack key={r.id} justify="space-between">
                      <Text fontSize="sm">{requirementLabel(r)}</Text>
                      <IconButton
                        aria-label={t("Remove") ?? "Remove"}
                        size="xs"
                        variant="ghost"
                        icon={<Icon as={FiX} />}
                        onClick={() =>
                          setRequirements((all) =>
                            all.filter((x) => x.id !== r.id),
                          )
                        }
                      />
                    </HStack>
                  ))}
                </Stack>
              )}
            </Box>
            <MarkedAnswers
              marketDialogueId={marketDialogueId}
              questions={questions}
            />
          </SimpleGrid>
        </Stack>
      </ModalBody>
      <ModalFooter gap="2">
        <Button variant="ghost" onClick={onClose}>
          {t("Cancel")}
        </Button>
        <Button
          colorScheme="teal"
          onClick={() => void create()}
          isLoading={isLoading}
        >
          {t("Create procurement")}
        </Button>
      </ModalFooter>
      {section && (
        <AddRequirementPanel
          isOpen
          onClose={() => setOpenLibrary(null)}
          type={section.type}
          title={section.button}
          typeNoun={section.noun}
          initialTab="library"
          lockedToInitialTab
          context={<MarkedAnswers marketDialogueId={marketDialogueId} questions={questions} asSectionLabel />}
          isAlreadyInSection={(el) => requirements.some((r) => r.id === el.id)}
          onPickElements={(picked) =>
            setRequirements((all) => [...all, ...picked])
          }
        />
      )}
    </>
  );
};

const requirementLabel = (element: LibraryElementDto) =>
  element.content.minimum?.title ??
  element.content.selection?.name ??
  element.content.award?.name ??
  "";

interface ChoiceItem {
  id: string;
  label: string;
  tags: (string | null)[];
}

const ChoiceSection = ({
  title,
  help,
  empty,
  items,
  selected,
  onChange,
}: {
  title: ReactNode;
  help?: ReactNode;
  empty: ReactNode;
  items: ChoiceItem[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) => {
  const toggle = (id: string, on: boolean) =>
    onChange(on ? [...selected, id] : selected.filter((s) => s !== id));
  return (
    <Box>
      <Heading as="h3" size="sm" mb="1">
        {title}
      </Heading>
      {help && (
        <Text fontSize="sm" color="smMuted" mb="2">
          {help}
        </Text>
      )}
      {items.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {empty}
        </Text>
      ) : (
        <Stack spacing="2">
          {items.map((item) => (
            <Checkbox
              key={item.id}
              isChecked={selected.includes(item.id)}
              onChange={(e) => toggle(item.id, e.target.checked)}
            >
              <HStack spacing="2">
                <Text>{item.label}</Text>
                {item.tags
                  .filter((tag): tag is string => tag !== null)
                  .map((tag) => (
                    <Tag key={tag} size="sm" variant="subtle">
                      {tag}
                    </Tag>
                  ))}
              </HStack>
            </Checkbox>
          ))}
        </Stack>
      )}
    </Box>
  );
};

const MarkedAnswers = ({ marketDialogueId, questions, asSectionLabel = false }: Props & { asSectionLabel?: boolean }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } =
    useListMarketDialogueAnswersQuery({ marketDialogueId });
  return (
    <Box data-testid="marked-answers">
      {asSectionLabel ? (
        <Text
          fontSize="xs"
          fontWeight="semibold"
          letterSpacing="0.06em"
          textTransform="uppercase"
          color="smMuted"
          mb="2"
        >
          {t("Answers taken forward")}
        </Text>
      ) : (
        <Heading as="h3" size="sm" mb="2">
          {t("Answers taken forward")}
        </Heading>
      )}
      {isLoading ? (
        <Spinner size="sm" />
      ) : isError || !data ? (
        <LoadFailed
          message={t("Could not load the answers.")}
          onRetry={() => void refetch()}
        />
      ) : (
        <TakenForwardAnswers
          questions={questions}
          answers={data.answers}
          empty={t("No answers are taken forward.")}
          answerMaxHeight="32"
        />
      )}
    </Box>
  );
};

const LoadFailed = ({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Alert status="error" rounded="md" fontSize="sm" alignItems="flex-start">
      <AlertIcon />
      <Stack spacing="2" align="flex-start">
        <Text>{message}</Text>
        <Button size="sm" onClick={onRetry}>
          {t("Try again")}
        </Button>
      </Stack>
    </Alert>
  );
};
